import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';
import { BudgetServiceOrderTerciary } from '../models/BudgetServiceOrderTerciary';

export interface BudgetServiceOrderCreate {
  serviceOrderWorkSectionId: number;
  osNumber?: string;
  status?: string;
  clientApproved?: boolean;
  plateVechile?: string;
}

export interface BudgetServiceOrderUpdate {
  serviceOrderWorkSectionId?: number;
  osNumber?: string;
  status?: string;
  clientApproved?: boolean;
  plateVechile?: string;
}

export interface BudgetLineInputPayload {
  description: string;
  quantity: number;
  unitValue: number;
  maintenanceKind?: string | null;
  chargePercent?: number | null;
  serviceTypeId?: number | null;
  itemId?: number | null;
  isGuincho?: boolean;
  externalServiceProviderId?: number | null;
}

export interface BudgetServiceOrderSearchFilters {
  workSectionId?: number;
  plate?: string;
  osNumber?: string;
  status?: string;
  clientApproved?: boolean;
}

export interface BudgetServiceOrderPaginatedResult {
  items: BudgetServiceOrderTerciary[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class BudgetServiceOrdersService {
  private readonly baseUrl = environment.microservices?.order || environment.apiUrl || '/api';
  private readonly resourcePath = 'bugetserviceorderterciaries';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    const userId = localStorage.getItem('lastLoggedUserId');
    if (userId) {
      headerMap['X-User-Id'] = userId;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;
    if (baseQuery) {
      url += `?${baseQuery}`;
    }
    return url;
  }

  /** GET /bugetserviceorderterciaries — primeira página (compatibilidade) */
  getAll(): Observable<BudgetServiceOrderTerciary[]> {
    return this.getPaginated(1, 100).pipe(map(res => res.items));
  }

  /**
   * GET /bugetserviceorderterciaries com paginação e filtros opcionais.
   * Query params: pageNumber, pageSize, workSectionId, plate, osNumber, status, clientApproved.
   */
  getPaginated(
    pageNumber: number = 1,
    pageSize: number = 20,
    filters?: BudgetServiceOrderSearchFilters
  ): Observable<BudgetServiceOrderPaginatedResult> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (filters?.workSectionId && filters.workSectionId > 0) {
      params = params.set('workSectionId', filters.workSectionId.toString());
    }
    if (filters?.plate?.trim()) {
      params = params.set('plate', filters.plate.trim());
    }
    if (filters?.osNumber?.trim()) {
      params = params.set('osNumber', filters.osNumber.trim());
    }
    if (filters?.status?.trim()) {
      params = params.set('status', filters.status.trim());
    }
    if (filters?.clientApproved !== undefined && filters?.clientApproved !== null) {
      params = params.set('clientApproved', String(filters.clientApproved));
    }

    return this.http
      .get<any>(this.buildUrl(this.resourcePath), { headers: this.getHeaders(), params })
      .pipe(
        map(res => this.mapPaginatedResponse(res, pageNumber, pageSize)),
        catchError(err => {
          if (err?.status === 404) {
            return of({
              items: [] as BudgetServiceOrderTerciary[],
              totalCount: 0,
              page: pageNumber,
              pageSize,
              totalPages: 1
            });
          }
          this.errorService.showErrorMessage('Erro ao listar orçamentos de serviços');
          return throwError(() => err);
        })
      );
  }

  /** GET /bugetserviceorderterciaries/{id} */
  getById(id: number): Observable<BudgetServiceOrderTerciary> {
    return this.http
      .get<any>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() })
      .pipe(
        map(res => this.mapFromDto(this.extractItem(res))),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar orçamento de serviço');
          return throwError(() => err);
        })
      );
  }

  /** GET /bugetserviceorderterciaries?workSectionId={serviceOrderWorkSectionId} */
  getByWorkSectionId(serviceOrderWorkSectionId: number): Observable<BudgetServiceOrderTerciary[]> {
    return this.getPaginated(1, 100, { workSectionId: serviceOrderWorkSectionId }).pipe(
      map(res => res.items)
    );
  }

  /** POST /bugetserviceorderterciaries */
  create(payload: BudgetServiceOrderCreate): Observable<BudgetServiceOrderTerciary> {
    return this.http
      .post<any>(this.buildUrl(this.resourcePath), payload, { headers: this.getHeaders() })
      .pipe(
        map(res => this.mapFromDto(this.extractItem(res))),
        tap(() => this.errorService.showSuccessMessage('Orçamento criado com sucesso')),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao criar orçamento de serviço');
          return throwError(() => err);
        })
      );
  }

  /** PUT /bugetserviceorderterciaries/{id} */
  update(id: number, payload: BudgetServiceOrderUpdate): Observable<BudgetServiceOrderTerciary> {
    return this.http
      .put<any>(this.buildUrl(`${this.resourcePath}/${id}`), payload, { headers: this.getHeaders() })
      .pipe(
        map(res => this.mapFromDto(this.extractItem(res))),
        tap(() => this.errorService.showSuccessMessage('Orçamento atualizado com sucesso')),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao atualizar orçamento de serviço');
          return throwError(() => err);
        })
      );
  }

  /** POST /bugetserviceorderterciaries/{id}/cancel — cancela orçamento e a OS. */
  cancel(
    id: number,
    options?: { cancelServiceOrder?: boolean }
  ): Observable<{ budgetId: number; serviceOrderId: number; status: string; serviceOrderStatus: string }> {
    const cancelServiceOrder = options?.cancelServiceOrder !== false;
    return this.http
      .post<any>(
        this.buildUrl(`${this.resourcePath}/${id}/cancel`),
        { cancelServiceOrder },
        { headers: this.getHeaders() }
      )
      .pipe(
        map(res => this.extractItem(res)),
        tap(() =>
          this.errorService.showSuccessMessage(
            cancelServiceOrder
              ? 'Orçamento e ordem de serviço cancelados.'
              : 'Orçamento cancelado.'
          )
        ),
        catchError(err => {
          const msg = err?.error?.message || err?.message || 'Erro ao cancelar o orçamento';
          this.errorService.showErrorMessage(msg);
          return throwError(() => err);
        })
      );
  }

  /** PUT /bugetserviceorderterciaries/{id}/linhas — substitui serviços e peças. */
  replaceLines(
    id: number,
    payload: { services: BudgetLineInputPayload[]; products: BudgetLineInputPayload[] }
  ): Observable<{ budgetId: number; status: string; totalGross: number }> {
    return this.http
      .put<any>(this.buildUrl(`${this.resourcePath}/${id}/linhas`), payload, { headers: this.getHeaders() })
      .pipe(
        map(res => this.extractItem(res)),
        tap(() => this.errorService.showSuccessMessage('Orçamento atualizado com sucesso')),
        catchError(err => {
          const msg = err?.error?.message || err?.message || 'Erro ao editar o orçamento';
          this.errorService.showErrorMessage(msg);
          return throwError(() => err);
        })
      );
  }

  /** DELETE /bugetserviceorderterciaries/{id} */
  delete(id: number): Observable<void> {
    return this.http
      .delete<void>(this.buildUrl(`${this.resourcePath}/${id}`), { headers: this.getHeaders() })
      .pipe(
        map(() => void 0),
        tap(() => this.errorService.showSuccessMessage('Orçamento removido com sucesso')),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao remover orçamento de serviço');
          return throwError(() => err);
        })
      );
  }

  private mapPaginatedResponse(
    res: any,
    pageNumber: number,
    pageSize: number
  ): BudgetServiceOrderPaginatedResult {
    const list = this.extractArray(res);
    const items = list.map(dto => this.mapFromDto(dto));
    const pagination = this.extractPagination(res);

    const totalCount =
      this.pickNestedNumber(pagination, [
        ['totalItems'], ['TotalItems'], ['totalCount'], ['TotalCount']
      ]) ??
      this.pickNestedNumber(res, [
        ['data', 'Pagination', 'TotalItems'], ['data', 'Pagination', 'totalItems'],
        ['data', 'pagination', 'totalItems'], ['data', 'pagination', 'TotalItems'],
        ['Data', 'Pagination', 'TotalItems'], ['Data', 'pagination', 'totalItems'],
        ['totalItems'], ['TotalItems'], ['totalCount'], ['TotalCount']
      ]) ??
      items.length;

    const page =
      this.pickNestedNumber(pagination, [
        ['pageNumber'], ['PageNumber'], ['page'], ['Page']
      ]) ??
      this.pickNestedNumber(res, [
        ['data', 'Pagination', 'PageNumber'], ['data', 'pagination', 'pageNumber'],
        ['Data', 'Pagination', 'PageNumber'], ['pageNumber'], ['PageNumber']
      ]) ??
      pageNumber;

    const size =
      this.pickNestedNumber(pagination, [
        ['pageSize'], ['PageSize']
      ]) ??
      this.pickNestedNumber(res, [
        ['data', 'Pagination', 'PageSize'], ['data', 'pagination', 'pageSize'],
        ['Data', 'Pagination', 'PageSize'], ['pageSize'], ['PageSize']
      ]) ??
      pageSize;

    const totalPages =
      this.pickNestedNumber(pagination, [
        ['totalPages'], ['TotalPages']
      ]) ??
      this.pickNestedNumber(res, [
        ['data', 'Pagination', 'TotalPages'], ['data', 'pagination', 'totalPages'],
        ['Data', 'Pagination', 'TotalPages'], ['totalPages'], ['TotalPages']
      ]) ??
      Math.max(1, Math.ceil(totalCount / size));

    return { items, totalCount, page, pageSize: size, totalPages };
  }

  /** Bloco Pagination do envelope { data: { Items, Pagination, Filters } }. */
  private extractPagination(res: any): any {
    return (
      res?.data?.Pagination ??
      res?.data?.pagination ??
      res?.Data?.Pagination ??
      res?.Data?.pagination ??
      res?.pagination ??
      res?.Pagination ??
      null
    );
  }

  private toNumber(value: any): number | undefined {
    if (value === undefined || value === null || value === '') {
      return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private pickNestedNumber(source: any, paths: string[][]): number | undefined {
    for (const path of paths) {
      let current = source;
      for (const key of path) {
        current = current?.[key];
        if (current === undefined || current === null) {
          break;
        }
      }
      const parsed = this.toNumber(current);
      if (parsed !== undefined) {
        return parsed;
      }
    }
    return undefined;
  }

  private extractArray(response: any): any[] {
    if (Array.isArray(response)) return response;

    // Envelope paginado atual: { data: { Items, Pagination, Filters } }
    if (Array.isArray(response?.data?.Items)) return response.data.Items;
    if (Array.isArray(response?.data?.items)) return response.data.items;
    if (Array.isArray(response?.Data?.Items)) return response.Data.Items;
    if (Array.isArray(response?.Data?.items)) return response.Data.items;

    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.data)) return response.data.data;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.Items)) return response.Items;
    if (Array.isArray(response?.bugetServiceOrderTerciaries)) return response.bugetServiceOrderTerciaries;
    if (Array.isArray(response?.BugetServiceOrderTerciaries)) return response.BugetServiceOrderTerciaries;
    if (Array.isArray(response?.data?.bugetServiceOrderTerciaries)) return response.data.bugetServiceOrderTerciaries;
    if (Array.isArray(response?.data?.BugetServiceOrderTerciaries)) return response.data.BugetServiceOrderTerciaries;
    return [];
  }

  private extractItem(response: any): any {
    return (
      response?.data?.data ??
      response?.data ??
      response?.item ??
      response?.bugetServiceOrderTerciary ??
      response?.BugetServiceOrderTerciary ??
      response
    );
  }

  private mapFromDto(dto: any): BudgetServiceOrderTerciary {
    return {
      id: dto?.Id ?? dto?.id ?? 0,
      osNumber: dto?.OSNumber ?? dto?.osNumber ?? dto?.OsNumber ?? '',
      serviceOrderWorkSectionId:
        dto?.ServiceOrderWorkSectionId ?? dto?.serviceOrderWorkSectionId ?? 0,
      status: dto?.Status ?? dto?.status ?? '',
      clientApproved: dto?.ClientApproved ?? dto?.clientApproved ?? false,
      plateVechile: dto?.PlateVechile ?? dto?.plateVechile ?? '',
      externalServiceProviderId:
        dto?.ExternalServiceProviderId ?? dto?.externalServiceProviderId ?? null,
      externalServiceProviderName:
        dto?.ExternalServiceProviderName ?? dto?.externalServiceProviderName ?? null,
      createdAt: dto?.CreatedAt ?? dto?.createdAt ?? undefined,
      updatedAt: dto?.UpdatedAt ?? dto?.updatedAt ?? undefined,
      createdBy: dto?.CreatedBy ?? dto?.createdBy ?? null,
      updatedBy: dto?.UpdatedBy ?? dto?.updatedBy ?? null
    };
  }
}
